import React from 'react';
import { AlertCircle } from 'lucide-react';
import type { RecordFormData } from './types';
import { RECORD_TYPES } from './types';

interface BasicRecordFormProps {
  formData: RecordFormData;
  setFormData: (data: RecordFormData) => void;
  errors: Record<string, string>;
  zoneName: string;
}

const BasicRecordForm: React.FC<BasicRecordFormProps> = ({
  formData,
  setFormData,
  errors,
  zoneName
}) => {
  const updateField = (field: keyof RecordFormData, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Name
        </label>
        <div className="relative">
          <input
            type="text"
            className={`input ${errors.name ? 'border-red-500' : ''}`}
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder={`@ (for ${zoneName}) or subdomain`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-slate-400">
            .{zoneName}
          </div>
        </div>
        {errors.name && (
          <div className="flex items-center space-x-2 mt-1 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.name}</span>
          </div>
        )}
      </div>

      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Type
        </label>
        <select
          className={`input ${errors.type ? 'border-red-500' : ''}`}
          value={formData.type}
          onChange={(e) => updateField('type', e.target.value)}
        >
          {RECORD_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        {errors.type && (
          <div className="flex items-center space-x-2 mt-1 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.type}</span>
          </div>
        )}
      </div>

      {/* Value */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Value
        </label>
        <textarea
          className={`input min-h-[60px] resize-y ${errors.value ? 'border-red-500' : ''}`}
          value={formData.value}
          onChange={(e) => updateField('value', e.target.value)}
          placeholder={getValuePlaceholder(formData.type)}
          rows={2}
        />
        {errors.value && (
          <div className="flex items-center space-x-2 mt-1 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.value}</span>
          </div>
        )}
      </div>

      {/* TTL */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          TTL (seconds)
        </label>
        <input
          type="number"
          className={`input ${errors.ttl ? 'border-red-500' : ''}`}
          value={formData.ttl}
          onChange={(e) => updateField('ttl', e.target.value)}
          min="1"
          placeholder="300"
        />
        {errors.ttl && (
          <div className="flex items-center space-x-2 mt-1 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.ttl}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const getValuePlaceholder = (type: string): string => {
  switch (type.toUpperCase()) {
    case 'A':
      return '192.0.2.1';
    case 'AAAA':
      return '2001:db8::1';
    case 'CNAME':
      return 'example.com';
    case 'MX':
      return 'mail.example.com';
    case 'TXT':
      return 'Text content';
    case 'NS':
      return 'ns1.example.com';
    case 'SRV':
      return 'target.example.com';
    default:
      return 'Enter record value...';
  }
};

export default BasicRecordForm;